import * as Sentry from '@sentry/browser'
import { type PaymentMethod, type Stripe, type StripeElements } from '@stripe/stripe-js'
import { useCallback, useState } from 'react'

type Props = {
  stripe: Stripe | null;
  elements: StripeElements | null;
}

export default function useCreateStripePaymentMethod ({ stripe, elements }: Props) {
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>()

  const onError = useCallback((error: string) => {
    setErrorMessage(error)
    setIsLoading(false)
  }, [])

  const createPaymentMethod = useCallback(
    async () => {
      if (!stripe || !elements) {
        return
      }

      setIsLoading(true)
      setErrorMessage(undefined)

      try {
        // Trigger form validation and wallet collection
        const { error: submitError } = await elements.submit()
        if (submitError) {
          console.error(submitError.message)
          Sentry.addBreadcrumb({
            category: 'Stripe',
            message: 'Stripe elements submit error',
            data: {
              error: submitError.message,
            },
          })
          onError(submitError.message ?? 'An error occurred')
          return
        }

        // Create the PaymentMethod using the card details from the Payment Element
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          elements,
        })

        if (error) {
          console.error(error.message)
          Sentry.addBreadcrumb({
            category: 'Stripe',
            message: 'Stripe payment method creation error',
            data: {
              error: error.message,
            },
          })
          onError(error.message ?? 'Failed to create payment method')
          return
        }

        if (paymentMethod) {
          // Validate that the card is issued in the US
          const cardCountry = paymentMethod.card?.country
          if (cardCountry && cardCountry !== 'US') {
            onError('Only US-issued cards are accepted. Please use a different card.')
            return
          }

          setPaymentMethod(paymentMethod)
          return paymentMethod
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred'
        console.error(message)
        Sentry.addBreadcrumb({
          category: 'Stripe',
          message: 'Stripe payment method creation unknown error',
          data: {
            error: message,
          },
        })
        onError(message)
      } finally {
        setIsLoading(false)
      }
    },
    [stripe, elements]
  )

  return {
    isLoading,
    errorMessage,
    paymentMethod,
    createPaymentMethod,
  }
}
